import { useState, useEffect } from 'react';
import { Table, Switch, Button, Drawer, Modal, Form, Input, Space, Typography, message, Tooltip } from 'antd';
import { ClockCircleOutlined, EnvironmentOutlined, UserOutlined, PlusOutlined, PictureOutlined } from '@ant-design/icons';
import { api } from '../api';
import OpeningHoursForm from './OpeningHoursForm';
import AddressForm from './AddressForm';
import ContactForm from './ContactForm';
import StoreImagesDrawer from './StoreImagesDrawer';

const { Text } = Typography;

const DAY_LABELS = { mo: 'Mo', tu: 'Di', we: 'Mi', th: 'Do', fr: 'Fr', sa: 'Sa', su: 'So' };

function formatOpenSlot(slot) {
  if (slot.closed) return `${DAY_LABELS[slot.fromDay]}–${DAY_LABELS[slot.toDay]}: Geschlossen`;
  const from = slot.openingHour || '?';
  const to = slot.closingHour || '?';
  return `${DAY_LABELS[slot.fromDay]}–${DAY_LABELS[slot.toDay]}: ${from}–${to}`;
}

function parseOpenJson(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try { return JSON.parse(raw); } catch { return []; }
}

export default function StoresTab({ isSupport, isBuchhalter }) {
  const [stores, setStores]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [openingHoursMap, setOpeningHoursMap] = useState({});

  const [drawer, setDrawer] = useState({ open: false, type: null, data: null, title: '' });
  const [newStoreModal, setNewStoreModal] = useState(false);
  const [newStoreLoading, setNewStoreLoading] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const [storeList, ohList] = await Promise.all([
        api.stores.list(),
        api.openingHours.list(),
      ]);
      setStores(storeList);
      const ohMap = {};
      for (const oh of ohList) ohMap[oh.id] = oh;
      setOpeningHoursMap(ohMap);
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (store, checked) => {
    try {
      await api.stores.setActive(store.id, checked);
      setStores(prev => prev.map(s => s.id === store.id ? { ...s, is_active: checked } : s));
      message.success(`${store.name} ist jetzt ${checked ? 'geöffnet' : 'geschlossen'}`);
    } catch (err) {
      message.error(err.message);
    }
  };

  const setBillingFlag = async (store, key, checked) => {
    const flags = {
      self_delivery: Boolean(store.self_delivery),
      uses_lieferservice_ec_terminal: Boolean(store.uses_lieferservice_ec_terminal),
      [key]: checked,
    };
    try {
      await api.stores.setBillingFlags(store.id, flags);
      setStores(prev => prev.map(s => s.id === store.id ? { ...s, ...flags } : s));
    } catch (err) {
      message.error(err.message);
    }
  };

  const openOhDrawer = async (store) => {
    let oh = openingHoursMap[store.opening_hours_id];
    if (!oh) {
      try {
        oh = await api.openingHours.create({ name: store.name, open_json: [] });
        await api.stores.update(store.id, {
          name: store.name, food_style: store.food_style,
          logo_url: store.logo_url, image_url: store.image_url,
          receipt_image_url: store.receipt_image_url,
          address_id: store.address_id, contact_id: store.contact_id,
          deliverer_id: store.deliverer_id, is_active: store.is_active,
          pos: store.pos, opening_hours_id: oh.id,
        });
        await load();
        oh = await api.openingHours.get(oh.id);
      } catch (err) {
        message.error(err.message);
        return;
      }
    }
    setDrawer({ open: true, type: 'oh', data: oh, title: `Öffnungszeiten – ${store.name}` });
  };

  const openAddressDrawer = async (store) => {
    if (!store.address_id) { message.warning('Keine Adresse verknüpft.'); return; }
    try {
      const addr = await api.addresses.get(store.address_id);
      setDrawer({ open: true, type: 'address', data: addr, title: `Adresse – ${store.name}` });
    } catch (err) { message.error(err.message); }
  };

  const createStore = async (values) => {
    setNewStoreLoading(true);
    try {
      await api.stores.create({ name: values.name.trim(), food_style: values.food_style?.trim() || null });
      message.success(`Store „${values.name.trim()}" angelegt`);
      form.resetFields();
      setNewStoreModal(false);
      load();
    } catch (err) {
      message.error(err.message);
    } finally {
      setNewStoreLoading(false);
    }
  };

  const openContactDrawer = async (store) => {
    if (!store.contact_id) { message.warning('Kein Kontakt verknüpft.'); return; }
    try {
      const contact = await api.contacts.get(store.contact_id);
      setDrawer({ open: true, type: 'contact', data: contact, title: `Kontakt – ${store.name}` });
    } catch (err) { message.error(err.message); }
  };

  const openImagesDrawer = (store) => {
    setDrawer({ open: true, type: 'images', data: store, title: `Bilder – ${store.name}` });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, store) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {store.logo_url && store.logo_url.startsWith('/api/public/images/') ? (
            <img
              src={store.logo_url}
              alt={name}
              style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 4, border: '1px solid #e8d5b0', flexShrink: 0 }}
            />
          ) : (
            <div style={{ width: 36, height: 36, background: '#f5ede0', borderRadius: 4, border: '1px dashed #e8d5b0', flexShrink: 0 }} />
          )}
          <Text strong>{name}</Text>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'is_active',
      width: 120,
      render: (_, store) => (
        <Switch
          checked={Boolean(store.is_active)}
          onChange={(checked) => toggleActive(store, checked)}
          checkedChildren="Offen"
          unCheckedChildren="Geschlossen"
        />
      ),
    },
    {
      title: 'Abrechnung',
      key: 'billing_flags',
      width: 200,
      render: (_, store) => (
        <Space direction="vertical" size={4}>
          <Tooltip title={isBuchhalter ? '' : 'Nur Buchhalter dürfen dies ändern'}>
            <Space size={6}>
              <Switch
                size="small"
                disabled={!isBuchhalter}
                checked={Boolean(store.self_delivery)}
                onChange={(checked) => setBillingFlag(store, 'self_delivery', checked)}
              />
              <Text style={{ fontSize: 12 }}>Selbstlieferer</Text>
            </Space>
          </Tooltip>
          <Tooltip title={isBuchhalter ? '' : 'Nur Buchhalter dürfen dies ändern'}>
            <Space size={6}>
              <Switch
                size="small"
                disabled={!isBuchhalter}
                checked={Boolean(store.uses_lieferservice_ec_terminal)}
                onChange={(checked) => setBillingFlag(store, 'uses_lieferservice_ec_terminal', checked)}
              />
              <Text style={{ fontSize: 12 }}>EC-Gerät v. Lieferservice</Text>
            </Space>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Öffnungszeiten',
      key: 'opening_hours',
      render: (_, store) => {
        const oh = openingHoursMap[store.opening_hours_id];
        if (!oh) return <Text type="secondary">–</Text>;
        const slots = parseOpenJson(oh.open_json);
        return (
          <Space direction="vertical" size={0}>
            {slots.map((slot, i) => (
              <Text key={i} style={{ fontSize: 12 }}>{formatOpenSlot(slot)}</Text>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Aktionen',
      key: 'actions',
      width: 220,
      render: (_, store) => (
        <Space>
          <Tooltip title="Bilder verwalten">
            <Button size="small" icon={<PictureOutlined />} onClick={() => openImagesDrawer(store)}>
              Bilder
            </Button>
          </Tooltip>
          <Tooltip title="Öffnungszeiten bearbeiten">
            <Button
              size="small"
              icon={<ClockCircleOutlined />}
              onClick={() => openOhDrawer(store)}
            >
              Zeiten
            </Button>
          </Tooltip>
          <Tooltip title="Adresse bearbeiten">
            <Button size="small" icon={<EnvironmentOutlined />} onClick={() => openAddressDrawer(store)}>
              Adresse
            </Button>
          </Tooltip>
          <Tooltip title="Kontakt bearbeiten">
            <Button size="small" icon={<UserOutlined />} onClick={() => openContactDrawer(store)}>
              Kontakt
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      {isSupport && (
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewStoreModal(true)}>
            Neuer Store
          </Button>
        </div>
      )}

      <Table
        rowKey="id"
        columns={columns}
        dataSource={stores}
        loading={loading}
        pagination={false}
        size="middle"
      />

      <Modal
        title="Neuer Store"
        open={newStoreModal}
        onCancel={() => { setNewStoreModal(false); form.resetFields(); }}
        onOk={() => form.submit()}
        okText="Anlegen"
        cancelText="Abbrechen"
        confirmLoading={newStoreLoading}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={createStore} style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name ist erforderlich' }]}>
            <Input placeholder="z. B. Muster Bistro" />
          </Form.Item>
          <Form.Item name="food_style" label="Küchenstil">
            <Input placeholder="z. B. Italienische Küche" />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={drawer.title}
        open={drawer.open}
        onClose={() => setDrawer(d => ({ ...d, open: false }))}
        width={520}
        destroyOnClose
      >
        {drawer.type === 'oh' && drawer.data && (
          <OpeningHoursForm
            openingHours={drawer.data}
            onSaved={() => { setDrawer(d => ({ ...d, open: false })); load(); }}
          />
        )}
        {drawer.type === 'address' && drawer.data && (
          <AddressForm
            address={drawer.data}
            onSaved={() => { setDrawer(d => ({ ...d, open: false })); load(); }}
          />
        )}
        {drawer.type === 'contact' && drawer.data && (
          <ContactForm
            contact={drawer.data}
            onSaved={() => { setDrawer(d => ({ ...d, open: false })); load(); }}
          />
        )}
        {drawer.type === 'images' && drawer.data && (
          <StoreImagesDrawer
            storeId={drawer.data.id}
            storeName={drawer.data.name}
            onChanged={load}
          />
        )}
      </Drawer>
    </>
  );
}
